import { Box, ColorOptions, Loader, Spacer, TextNew } from '@jfs/ui-new'
import { isEmpty } from '@jfs/utils'
import { FC, useEffect, useState } from 'react'
import { SubscriptionPaymentTitles } from '../../data/static/data'
import { dateFormatter, DateFormatTypes, formatAMPM } from '../../util/util'
import { ModBox } from '../ModifiedBox/ModifiedBox'
import { TableComponent } from '../TableComponent'
import { styles } from './styles'

const onMainViewPress = (setExpanded: (val: boolean) => void, isExpanded: boolean) => {
  setExpanded(!isExpanded)
}

interface AccordionItemProps {
  data: any
  onRefund: (id: string) => void
  onPaymentHistory: (trxId: string) => void
  index: any
  loading: any
  onPress: () => void
  selectedIndex: number
  type: number
}

const getTextColor = (status: string) => {
  type colorType = {
    [key: string]: any
  }

  const getColor: colorType = {
    PAID: 'GREEN_C',
    SUCCESS: 'GREEN_C',
    INVOICED: 'RED_C',
    FAILED: 'RED_C',
    SUSPENDED: 'ORANGE_C',
    CANCELED: 'RED_C',
    ACTIVE: 'GREEN_C',
    FULFILLED: 'GREEN_C',
    INITIATED: 'GREEN_C',
    PENDING: 'ORANGE_C',
    'IN PROGRESS': 'GREEN_C',
  }
  return getColor[status] ?? ''
}

const AccordionItem: FC<AccordionItemProps> = ({
  data,
  onRefund,
  onPaymentHistory,
  index,
  loading,
  onPress,
  selectedIndex,
  type,
}: AccordionItemProps) => {
  const { status, id, amount, transactionDate, invoice } = data
  const [isExpanded, setExpanded] = useState(false)

  useEffect(() => {
    if (selectedIndex !== index) {
      setExpanded(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex])

  const onItemPress = () => {
    onPress()
    if (!isExpanded) {
      onPaymentHistory(id)
    }
    onMainViewPress(setExpanded, isExpanded)
  }

  const getDate = () => {
    return `${dateFormatter(transactionDate, DateFormatTypes.MONTH_STRING)} | ${formatAMPM(new Date(transactionDate))}`
  }

  return (
    <>
      <Box {...styles.AccordionWrapper} onPress={onItemPress} testID={`subscriptionDetailsAccordionItem${index}`}>
        <Box f={0.3}>
          <TextNew {...styles.boldHeaderText}>{`${invoice}`}</TextNew>
          <TextNew {...styles.lightColoredDesc}>{`${id}`}</TextNew>
        </Box>
        <Box f={0.3} justifyContent="center">
          <TextNew {...styles.expandedListHeader}>{getDate()}</TextNew>
        </Box>
        <Box f={0.2} justifyContent="center">
          <TextNew {...styles.amountText}>{`₹ ${amount}`}</TextNew>
        </Box>
        <Box f={0.2} justifyContent="center">
          <Box {...styles[status]} alignSelf="flex-start" ph={8} pv={4} borderRadius={4}>
            <TextNew {...styles.invoiceText} color={getTextColor(status)}>
              {status}
            </TextNew>
          </Box>
        </Box>
      </Box>
      {isExpanded && renderExpandedView(data, onRefund, index, loading, type)}
    </>
  )
}

const renderExpandedView = (data: any, onRefund: (id: string) => void, index: number, loading: any, type: number) => {
  if (loading?.isLoading && index === loading?.index) {
    return (
      <>
        <Spacer size={16} />
        <Loader />
      </>
    )
  }

  const length = data?.paymentHistory?.length

  if (length === 0) {
    return (
      <Box padding={16} alignItems="center" bg="LIGHT_GREY_SEPARATOR">
        <TextNew {...styles.expandedListHeader}>{'No records'}</TextNew>
      </Box>
    )
  }

  return renderPaymentHistory(data, onRefund, type)
}

const renderPaymentHistory = (data: any, onRefund: (id: string) => void, type: number) => {
  return (
    <Box bg="LIGHT_GREY_SEPARATOR">
      <PaymentHistory data={data} onRefund={onRefund} type={type} />
    </Box>
  )
}

const getStatusStyle = (key: string) => {
  if (key === 'Status') {
    return { pv: 2, ph: 8, alignSelf: 'flex-start' }
  }
  return {}
}

const renderLabels = (item: any, details?: string) => {
  if (isEmpty(item?.key) || isEmpty(item?.value)) {
    return null
  }
  return (
    <Box flexDirection="row" flexGrow={1} pv={4}>
      <Box f={0.4}>
        <TextNew {...styles.expandedListKey}>{item?.key}</TextNew>
      </Box>
      <Spacer size={16} direction="horizontal" />
      <Box f={0.6}>
        <Box {...styles[item?.value]} {...getStatusStyle(item?.key)}>
          <TextNew {...styles.expandedListHeader} color={getTextColor(item?.value)}>
            {item?.value}
          </TextNew>
        </Box>
        {details && <TextNew {...styles.expandedListKey}>{details}</TextNew>}
      </Box>
    </Box>
  )
}

const renderDetailsView = (data: any, isAuthTransaction: boolean, onRefund?: (id: string) => void) => {
  const onRefunded = () => {
    if (onRefund) {
      onRefund(data?.transactionId)
    }
  }

  const getTitle = (key: string) => {
    return SubscriptionPaymentTitles[key as keyof typeof SubscriptionPaymentTitles]
  }

  const {
    payerVpa,
    payerName,
    payerAccountNumber,
    payerBankIfsc,
    payerBankName,
    payerAccountType,
    payerBankId,
    payerCardType,
    payerCardScheme,
    payerCardProduct,
    payerCardName,
    payerCardMaskedNumber,
  } = data?.paymentDetails ?? {}

  const disableRefund = () => {
    return +data?.refund === +data?.amount
  }

  const paymentDetails = () => {
    if (!isEmpty(payerVpa)) {
      let str = `VPA - ${payerVpa}`
      if (!isEmpty(payerName)) {
        str = str + `,\nName - ${payerName}`
      }
      return str
    }

    if (!isEmpty(payerBankName)) {
      let str = payerBankName
      if (!isEmpty(payerAccountNumber)) {
        str = isEmpty(str) ? payerAccountNumber : str + `,\n${payerAccountNumber}`
      }
      if (!isEmpty(payerBankIfsc)) {
        str = isEmpty(str) ? payerBankIfsc : str + `,\n${payerBankIfsc}`
      }
      if (!isEmpty(payerAccountType)) {
        str = isEmpty(str) ? payerAccountType : str + `,\n${payerAccountType}`
      }
      if (!isEmpty(payerBankId)) {
        str = isEmpty(str) ? payerBankId : str + `,\n${payerBankId}`
      }
      return str
    }

    if (!isEmpty(payerCardScheme) && !isEmpty(payerCardMaskedNumber)) {
      let str = `${payerCardScheme} card ending ${payerCardMaskedNumber}`
      if (!isEmpty(payerCardName)) {
        str = str + `,\nName on card - ${payerCardName}`
      }
      return str
    }
    return ''
  }

  return (
    <Box flexGrow={1} flexDirection="row" justifyContent="space-between" p={16}>
      <Box f={1.1} alignSelf="flex-start">
        {renderLabels({ key: getTitle('referenceID'), value: data?.referenceId })}
        {renderLabels({ key: getTitle('paymentID'), value: data?.transactionId })}
        {renderLabels({ key: getTitle('status'), value: data?.status })}
        {renderLabels({ key: getTitle('paymentmode'), value: payerCardType ? payerCardType : 'UPI' }, paymentDetails())}
      </Box>
      <Spacer size={16} direction="horizontal" />
      <Box width={'1'} backgroundColor={'GREY_EB' as ColorOptions} />
      <Spacer size={16} direction="horizontal" />
      <Box f={0.9} justify="space-between">
        <Box>
          {data?.status === 'INVOICED' &&
            renderLabels({
              key: getTitle('transactionDate'),
              value: dateFormatter(data?.transactionDate, DateFormatTypes.MONTH_STRING),
            })}
          {renderLabels({ key: getTitle('amount'), value: isEmpty(data?.amount) ? 'NA' : `₹ ${data?.amount}` })}
          {renderLabels({ key: getTitle('addons'), value: !isEmpty(data?.addons) ? `₹ ${data?.addons}` : '' })}
        </Box>
        {data?.status === 'SUCCESS' && !isAuthTransaction && (
          <Box
            {...styles.refundStyles}
            disabled={disableRefund()}
            onPress={onRefunded}
            backgroundColor={disableRefund() ? ('GREY_E5' as ColorOptions) : 'BLUE'}
            testID={'subscriptionsInitiateRefund'}>
            <TextNew type="body12" fontFamily="jiotype-medium" color={'WHITE'} textAlign="left">
              Issue Refund
            </TextNew>
          </Box>
        )}
      </Box>
    </Box>
  )
}

interface PaymentHistoryItemProps {
  count: number
  onRefund: (id: string) => void
  data: any
  onPress: () => void
  index: number
  selectedIndex?: number
  length: number
  methodType: string
  type: number
}

const PaymentHistoryItem: FC<PaymentHistoryItemProps> = (props) => {
  const { count, onRefund, data, onPress, index, selectedIndex, length, methodType, type } = props
  const [isExpanded, setExpanded] = useState(length === count)

  useEffect(() => {
    if (selectedIndex !== -1 && selectedIndex !== index) {
      setExpanded(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex])

  const onItemPress = () => {
    onPress()
    onMainViewPress(setExpanded, isExpanded)
  }
  const isAuthTranaction = methodType === '310' && type === 2

  return (
    <Box backgroundColor="WHITE" borderRadius={4}>
      {length !== 1 && (
        <Box {...styles.listItemContainer} bg={isExpanded ? ('BLUE_0A' as ColorOptions) : 'WHITE'}>
          <TextNew
            {...styles.expandedListHeader}
            color={isExpanded ? ('BLUE' as ColorOptions) : 'BLACK_21'}>{`Attempt ${count}`}</TextNew>
          <Spacer size={8} direction="horizontal" />
          <Box
            onPress={onItemPress}
            testID="hideShow"
            accessibilityLabel={isExpanded ? 'expanded' : 'collapsed'}
            underlayColor="LIGHT_GREY_SEPARATOR">
            <TextNew {...styles.expandedListHeader} textDecorationLine="underline" color={'BLUE' as ColorOptions}>
              {isExpanded ? 'hide' : 'show'}
            </TextNew>
          </Box>
        </Box>
      )}
      {length === 1 && (
        <ModBox p={16} show={data?.status === 'INVOICED' || isAuthTranaction}>
          <TextNew {...styles.expandedListHeader}>
            {data?.status === 'INVOICED' ? 'Payment Pre-Request' : isAuthTranaction ? 'Account Authentication' : ''}
          </TextNew>
        </ModBox>
      )}
      {isExpanded && renderDetailsView(data, isAuthTranaction, onRefund)}
    </Box>
  )
}

interface PaymentHistoryProps {
  data: any
  onRefund: (id: string) => void
  type: number
}

const PaymentHistory: FC<PaymentHistoryProps> = ({ data, onRefund, type }: PaymentHistoryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const { paymentHistory, methodType } = data

  const _renderItem = ({ item, index }: { item: any; index: number }) => {
    const getCount = () => {
      return paymentHistory?.length - index
    }

    const onPress = () => {
      setSelectedIndex(index)
    }

    return (
      <PaymentHistoryItem
        count={getCount()}
        onRefund={onRefund}
        data={item}
        onPress={onPress}
        index={index}
        selectedIndex={selectedIndex}
        length={paymentHistory?.length}
        methodType={methodType}
        type={type}
      />
    )
  }

  return (
    <Box ph={16} pv={16} backgroundColor={'BLACK_21_0D' as ColorOptions}>
      <TableComponent
        value={''} //Routes value
        canFetchMore={false}
        ListContent={paymentHistory}
        hide={false}
        isCustom={true}
        RenderCustomElement={_renderItem}
        contentContainerStyle={styles.flatlist}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps={'always'}
        ItemSeparatorComponent={() => <Box p={8} />}
        extraData={selectedIndex}
      />
    </Box>
  )
}

export { AccordionItem }
